import React, { useEffect } from "react";
import {
  DEFAULT_EQUIPMENT,
  DEFAULT_LABORATORY,
  DEFAULT_VALID_PREPARATION,
} from "@app/pages/samples/utils/constants";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ProcedureType, UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
} from "@app/types/samples.types";
import { useEquipment } from "@services/servicesGenericsTables";
import useTabs from "@app/pages/samples/utils/useTab";
import { useSample } from "@app/pages/samples/utils/useSample";
import useAuth from "@app/context/AuthContext";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { formatDateOrNow } from "@app/utils/formatDate";
import { setsAnalysisPhasesBT } from "@app/pages/samples/config/phases/AnalysisPhases";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentMeasurement?: AnalysisCurrent,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  typeAnalysis?: any,
  filteredProceduresPRE?: ProcedureType
) => {
  const currentMeasurementIndex: any =
    currentPreparationIndex?.aa_measures?.[currentMeasurement?.index as number];

  const currentCountingIndex: any =
    currentMeasurementIndex?.countings?.[currentCounting?.index as number];
  const currentCalculationIndex =
    currentCountingIndex?.calculations?.[currentCalculation?.index as number];
  const currentSampling = sampling?.current;
  const defaultTypeUnit = sampling?.current?.unit_types;

  const consultEquipments = (ids: number[]) => {
    if (!ids) return [];
    return ids.map((id) =>
      useEquipment
        .getState()
        .equipmentList.find((equipment) => equipment.id === id)
    );
  };
  const processingCurrent =
    useSample.getState().stateSample?.processing?.current || [];

  const dryingProcessing =
    processingCurrent && processingCurrent.length > 0
      ? processingCurrent.find((item) => item.processing_category?.id === 3)
      : undefined;

  return {
    analysis_analyst: currentPreparationIndex?.analyst,
    //preparation
    preparation_procedure:
      currentPreparationIndex?.preparation_procedure ||
      filteredProceduresPRE ||
      undefined,
    start_date: currentPreparationIndex?.start_date || dayjs().toISOString(),
    analysis_amount_sample: currentPreparationIndex?.amount_sample || 0,
    analysis_equipment: currentPreparationIndex?.equipment || [],
    scintillator_lot_number:
      currentPreparationIndex?.scintillator_lot_number || undefined,
    analysis_unit_types: currentPreparationIndex?.unit_types || defaultTypeUnit,
    valid_preparation:
      currentPreparationIndex?.valid_preparation || DEFAULT_VALID_PREPARATION,
    mg_torta: 300,
    planchette: currentPreparationIndex?.planchette,
    planchette_with_precipitate:
      currentPreparationIndex?.planchette_with_precipitate,
    planchette_with_precipitate_1:
      currentPreparationIndex?.planchette_with_precipitate_1,
    planchette_with_precipitate_2:
      currentPreparationIndex?.planchette_with_precipitate_2,
    planchette_with_precipitate_3:
      currentPreparationIndex?.planchette_with_precipitate_3,
    planchette_with_precipitate_4:
      currentPreparationIndex?.planchette_with_precipitate_4,
    planchette_with_precipitate_5:
      currentPreparationIndex?.planchette_with_precipitate_5,
    //mesurement
    measurement_analyst: currentMeasurementIndex?.analyst || user,
    measurement_procedure: currentMeasurementIndex?.procedure || undefined,
    measurement_start_date_time: formatDateOrNow(
      currentMeasurementIndex?.start_date_time
    ),
    measurement_equipment:
      consultEquipments(
        currentMeasurementIndex?.equipment?.map((e: any) => e.id)
      ) || [],
    measurement_dilution_factor:
      currentMeasurementIndex?.dilution_factor || null,
    measurement_aa_equipment_result:
      currentMeasurementIndex?.aa_equipment_result || null,
    measurement_result_to_calculation:
      currentMeasurementIndex?.result_to_calculation || null,
    measurement_mg_potasio: currentMeasurementIndex?.mg_potasio || null,
    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure: currentCountingIndex?.counting_procedure || undefined,
    counting_equipment: currentCountingIndex?.equipment
      ? currentCountingIndex?.equipment
      : typeAnalysis
      ? DEFAULT_EQUIPMENT[`${typeAnalysis?.id}`]
      : [],
    counting_detector: currentCountingIndex?.detectors || undefined,
    counting_date: currentCountingIndex?.counting_date || dayjs().toISOString(),
    first_counting_time: currentCountingIndex?.first_counting_time || undefined,
    first_counting_cpm: currentCountingIndex?.first_counting_cpm || undefined,
    second_counting_cpm: currentCountingIndex?.second_counting_cpm || undefined,
    total_sample_counting_time: currentCountingIndex?.total_sample_counting_time || undefined,
    counting_valid: "NA",

    //calculation
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_equipment: currentCountingIndex?.equipment || [
      currentCountingIndex?.equipo,
    ],
    calculation_analyst: currentCalculationIndex?.analyst || user,
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_amount_sample:
      currentPreparationIndex?.amount_sample || undefined,
    calculation_units: currentPreparationIndex?.unit_types || undefined,
    calculation_planchette_with_precipitate:
      currentPreparationIndex?.planchette_with_precipitate || undefined,
    calculation_mg_potasio: currentMeasurementIndex?.mg_potasio || null,
    calculation_tsie_sample:
      currentCalculationIndex?.sqp_tsie_sample || dryingProcessing?.wet_dry,
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || undefined,

    calculation_detector: currentCountingIndex?.detectors || undefined,
    calculation_first_counting_date: formatDateOrNow(
      currentCountingIndex?.first_counting_date
    ),
    calculation_counting_date: currentCountingIndex?.counting_date
      ? formatDateOrNow(currentCountingIndex?.counting_date)
      : undefined,
    total_time_counting: currentCountingIndex?.total_sample_counting_time || undefined,
    calculation: currentCalculationIndex?.calculation || "",
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    calculation_method: currentCalculationIndex?.calculation_method || "ISO",
    calculation_fondo: currentCalculationIndex?.fondos || undefined,
    calculation_fondo_alfa: currentCalculationIndex?.fondo_alfa || undefined,
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
  };
};

const BTForm = () => {
  const { tabValueAnalysis } = useTabs();
  const filteredProceduresPRE = useFilteredProcedures("PRE");

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { user } = useAuth();

  const {
    currentPreparation,
    currentMeasurement,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const currentPreparationObject = preparationsList.filter((preparation) =>
    preparation.analysis_type.some(
      (analysis) => analysis.id === form.getValues("typeAnalysis")?.id
    )
  )[currentPreparation.index as number];
  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentMeasurement,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          form.getValues("typeAnalysis"),
          filteredProceduresPRE?.[0]
        ),
      };

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]); // eslint-disable-line

  const CurrentComponent = setsAnalysisPhasesBT[tabValueAnalysis]?.component;
  return CurrentComponent ? <CurrentComponent /> : null;
};

export default BTForm;
